import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { useClientes, useEnviosDeEmail } from '../../shared/api/queries.ts';
import type { Cliente, SituacaoDeEnvio } from '../../shared/api/tipos.ts';
import { SeletorDeCompetencia } from '../../shared/ui/SeletorDeCompetencia.tsx';
import { useSessao } from '../auth/sessao.tsx';
import { DialogoRevogarLinks } from './DialogoRevogarLinks.tsx';
import { TabelaDeEnvios } from './TabelaDeEnvios.tsx';

/**
 * O log de e-mails do escritório.
 *
 * Uma lista só, com **todos** os envios do sistema: o automático do dia 5 e o
 * manual da tela de arquivos fiscais, lado a lado. Quem pergunta "vocês
 * mandaram as notas de setembro da loja tal?" não sabe por qual caminho a
 * mensagem saiu — separar em duas telas seria pedir justamente a informação
 * que a pessoa está procurando.
 *
 * O filtro de mês abre em "todos" de propósito: esta tela é o log, e quem
 * chega aqui costuma não saber de que competência era a mensagem. Quem já
 * sabe o mês tem a aba de histórico em Pendências, que nasce recortada.
 *
 * É **só leitura**, e por isso serve a todo mundo que entra no sistema: quem
 * atende o telefone precisa responder "saiu?" sem depender de um
 * administrador. Mandar e-mail se faz onde a ação tem contexto, na tela de
 * arquivos fiscais, com cliente e período na frente de quem clica; um botão de
 * disparo no meio de um histórico manda sem que ninguém tenha olhado para o
 * que vai sair. O que sobra aqui é fechar um link antes do prazo — decisão
 * sobre o que já foi enviado, e essa é de administrador.
 *
 * A URL do contador não aparece: ela existe uma vez só, na resposta que a
 * emitiu, como o token do agente.
 */
export function EnviosDeEmail() {
  const clientes = useClientes();
  const { usuario } = useSessao();
  // Esconder o botão é só para a tela não oferecer o que não funciona: quem
  // recusa a revogação é a rota da API, que exige administrador.
  const podeRevogar = usuario?.papel === 'administrador';

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [situacao, setSituacao] = useState<SituacaoDeEnvio | ''>('');
  const [competencia, setCompetencia] = useState('');
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(50);

  const { data, isPending, isError, error, isFetching } = useEnviosDeEmail(
    {
      empresaId: cliente?.id,
      situacao: situacao === '' ? undefined : situacao,
      competencia: competencia === '' ? undefined : competencia,
    },
    { limite: porPagina, deslocamento: pagina * porPagina },
  );

  const [revogando, setRevogando] = useState<{
    empresaId: string;
    razaoSocial: string;
  } | null>(null);
  const [revogados, setRevogados] = useState<number | null>(null);

  const itens = data?.itens ?? [];
  const total = data?.total ?? 0;

  return (
    <Stack spacing={2}>
      <Card>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 240 }}>
            <Typography variant="h2">Envios de e-mail</Typography>
            <Typography variant="body2" color="text.secondary">
              Tudo que o sistema mandou: o fechamento automático do dia 5 e os envios feitos
              na tela de arquivos fiscais.
            </Typography>
          </Box>

          {/* O mesmo seletor da tela de arquivos fiscais: com dezenas de
              clientes, um menu de rolagem obriga a procurar pela razão social
              com o olho, e aqui se chega sabendo de qual loja é a pergunta.
              Vazio significa todos — a leitura de log mais comum é a do dia,
              sem filtro. */}
          <Autocomplete
            options={clientes.data?.itens ?? []}
            value={cliente}
            onChange={(_, escolhido) => {
              setCliente(escolhido);
              setPagina(0);
            }}
            getOptionLabel={(opcao) => opcao.razaoSocial}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={clientes.isPending}
            noOptionsText="Nenhum cliente cadastrado."
            size="small"
            sx={{ minWidth: 300 }}
            renderInput={(parametros) => (
              <TextField {...parametros} label="Cliente" placeholder="Todos os clientes" />
            )}
            renderOption={(parametros, opcao) => {
              const { key, ...resto } = parametros;
              return (
                <li key={key} {...resto}>
                  <Stack>
                    <Typography variant="body2">{opcao.razaoSocial}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opcao.cnpjFormatado}
                    </Typography>
                  </Stack>
                </li>
              );
            }}
          />

          {/* A competência casa pelo campo do fechamento **ou** pelo período
              que a mensagem anunciou: o envio manual não grava mês nenhum, e
              sem a segunda metade da regra ele sumiria do filtro. */}
          <SeletorDeCompetencia
            valor={competencia}
            aoMudar={(escolhida) => {
              setCompetencia(escolhida);
              setPagina(0);
            }}
            permitirTodos
          />

          <TextField
            select
            label="Situação"
            size="small"
            value={situacao}
            onChange={(evento) => {
              setSituacao(evento.target.value as SituacaoDeEnvio | '');
              setPagina(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="enviado">Enviados</MenuItem>
            <MenuItem value="falhou">Falhas</MenuItem>
          </TextField>
        </Toolbar>

        <Divider />

        <CardContent>
          <TabelaDeEnvios
            itens={itens}
            carregando={isPending}
            recarregando={isFetching}
            erro={isError ? error : undefined}
            vazio="Nenhum e-mail registrado com este filtro. O envio automático roda no dia 5, e o da tela de arquivos fiscais aparece aqui no instante em que sai."
            aoRevogar={podeRevogar ? setRevogando : undefined}
          />
        </CardContent>

        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            onPageChange={(_, nova) => setPagina(nova)}
            rowsPerPage={porPagina}
            onRowsPerPageChange={(evento) => {
              setPorPagina(Number(evento.target.value));
              setPagina(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}
      </Card>

      {revogados !== null && (
        <Alert severity="success" onClose={() => setRevogados(null)}>
          {revogados === 1 ? '1 link revogado.' : `${revogados} links revogados.`}
        </Alert>
      )}

      <DialogoRevogarLinks
        cliente={revogando}
        aoFechar={() => setRevogando(null)}
        aoRevogado={setRevogados}
      />
    </Stack>
  );
}
